import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { Answer } from '@/types'

export interface SingleNpsState {
  answers: Answer[]
  checkedOptions: Record<string, number>
  activeTab: number
}

export type MultiNpsState = Record<string, SingleNpsState>

const PERSIST_KEY = 'npsStates'
const fromStorage: MultiNpsState = (() => {
  try {
    return JSON.parse(localStorage.getItem(PERSIST_KEY) || '{}')
  } catch {
    return {}
  }
})()

const defaultState: SingleNpsState = {
  answers: [],
  checkedOptions: {},
  activeTab: 0,
}

const npsSlice = createSlice({
  name: 'nps',
  initialState: fromStorage,
  reducers: {
    setAnswers(state, action: PayloadAction<{ id: number; answers: Answer[] }>) {
      const { id, answers } = action.payload
      const prev = state[id] ?? defaultState
      state[id] = { ...prev, answers }
      localStorage.setItem(PERSIST_KEY, JSON.stringify(state))
    },
    setCheckedOptions(
      state,
      action: PayloadAction<{ id: number; checkedOptions: Record<string, number> }>,
    ) {
      const { id, checkedOptions } = action.payload
      const prev = state[id] ?? defaultState
      state[id] = { ...prev, checkedOptions }
      localStorage.setItem(PERSIST_KEY, JSON.stringify(state))
    },
    setActiveTab(state, action: PayloadAction<{ id: number; activeTab: number }>) {
      const { id, activeTab } = action.payload
      const prev = state[id] ?? defaultState
      state[id] = { ...prev, activeTab }
      localStorage.setItem(PERSIST_KEY, JSON.stringify(state))
    },
    resetNps(state, action: PayloadAction<number>) {
      const id = action.payload
      delete state[id]
      localStorage.setItem(PERSIST_KEY, JSON.stringify(state))
    },
  },
})

export const { setAnswers, setCheckedOptions, setActiveTab, resetNps } = npsSlice.actions

export const selectNpsById = (
  state: { nps: MultiNpsState },
  id: number,
): SingleNpsState => state.nps[id] ?? defaultState

export default npsSlice.reducer
